import { useCallback, useMemo } from "react";
import { useListQueryParams } from "hooks";
import {
  SearchTextParamName,
  SortDirection,
  Template,
  TemplateTypeKeys,
} from "types";
import {
  FilterField,
  filterEntities,
  filterEntityByField,
  getComparisonType,
  getSearchFields,
  getSortFields,
  sortEntities,
  sortEntitiesByField,
} from "utils/tables";

const searchFields = getSearchFields<Template>(TemplateTypeKeys);

export const useTemplateListFilterAndSort = (filter = true) => {
  const searchParams = useListQueryParams();

  const filterFields: FilterField<Template>[] = useMemo(() => {
    const defaultFilters: Partial<Record<keyof Template, string>> = {};

    const filters: FilterField<Template>[] = Array.from(
      searchParams.entries(),
    ).map(([field, value]) => ({
      field: field as keyof Template,
      value,
      comparisonType: getComparisonType(searchParams, field),
    }));

    const defaultFilterFields: FilterField<Template>[] = Object.entries(
      defaultFilters,
    )
      .filter(
        ([key, value]) =>
          value !== undefined &&
          !filters.some((filterField) => filterField.field === key),
      )
      .map(([field, value]) => ({
        field: field as keyof Template,
        value,
        comparisonType: getComparisonType(searchParams, field),
      }));

    const allFilters = [...filters, ...defaultFilterFields];

    return allFilters;
  }, [searchParams]);

  const sortFields = useMemo(() => {
    const defaultSorts: Partial<Record<keyof Template, SortDirection>> = {
      name: SortDirection.asc,
    };

    return getSortFields<Template>(searchParams.get("sort"), defaultSorts);
  }, [searchParams]);

  const filterAndSortTemplates = useCallback(
    (
      templateList: Template[],
      customSortFunctions: Partial<
        Record<
          keyof Template,
          (a: Template, b: Template, direction: SortDirection) => number
        >
      > = {},
      customFilterFunctions: Partial<
        Record<keyof Template, (entity: Template, value: string) => boolean>
      > = {},
    ) =>
      sortEntities<Template>(
        filter
          ? filterEntities<Template>(
              templateList,
              filterFields,
              searchFields,
              searchParams.get(SearchTextParamName.SearchTemplate),
              filterEntityByField<Template>,
              TemplateTypeKeys,
              customFilterFunctions,
            )
          : templateList,
        sortFields,
        sortEntitiesByField<Template>,
        TemplateTypeKeys,
        customSortFunctions,
      ).map((template) => ({
        ...template,
        updatedAt: new Date(
          new Date(template.updatedAt ?? new Date().getTime()).getTime() + 1000,
        ).toISOString(),
      })),
    [filter, filterFields, searchParams, sortFields],
  );

  return filterAndSortTemplates;
};
